/**
 * Preferences, and the two housekeeping buttons that belong nowhere else.
 *
 * Every choice is a row of options rather than a dropdown: there are never more
 * than five, they all fit, and seeing the alternatives is the point.
 */

import { useRef, useState } from "react";

import {
  BackupError,
  backupFilename,
  exportBackup,
  importBackup,
} from "../features/backup/backup";
import type { ImportResult } from "../features/backup/backup";
import { todayIso } from "../lib/date/isoDate";
import { clearRecognitionCache } from "../features/vision/recognitionCache";
import { isVisionConfigured } from "../features/vision/config";
import { checkVision } from "../features/vision/checkVision";
import type { VisionCheck } from "../features/vision/checkVision";
import { useSettings } from "../features/settings/SettingsContext";
import {
  ALERT_LABELS,
  COOKING_LABELS,
  COOKING_LEVELS,
  DIETS,
  DIET_LABELS,
  EATING_GOALS,
  EATING_LABELS,
  EXPIRY_ALERTS,
  THEMES,
  THEME_LABELS,
} from "../features/settings/types";
import type { Settings } from "../features/settings/types";
import { BUILD } from "../version";
import { Icon } from "./Icon";

import "./SettingsScreen.css";

interface ChoiceRowProps<T extends string> {
  label: string;
  hint?: string;
  options: readonly T[];
  labels: Record<T, string>;
  value: T;
  onChange: (value: T) => void;
}

function ChoiceRow<T extends string>({
  label,
  hint,
  options,
  labels,
  value,
  onChange,
}: ChoiceRowProps<T>) {
  return (
    <fieldset className="setting">
      <legend className="setting__label">{label}</legend>
      {hint && <p className="setting__hint">{hint}</p>}
      <div className="setting__options">
        {options.map((option) => (
          <button
            className="setting__option"
            key={option}
            type="button"
            aria-pressed={value === option}
            onClick={() => onChange(option)}
          >
            {labels[option]}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

interface SettingsScreenProps {
  /** Told when an import replaces the inventory, so the shell can re-read it. */
  onDataReplaced: () => void;
}

export function SettingsScreen({ onDataReplaced }: SettingsScreenProps) {
  const { settings, update } = useSettings();
  const [cacheCleared, setCacheCleared] = useState(false);
  const [check, setCheck] = useState<VisionCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [backupNote, setBackupNote] = useState<string | null>(null);
  const [backupFailed, setBackupFailed] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function download() {
    const backup = exportBackup();
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    // An anchor with `download` is the only way to save a file from a page
    // without a server to hand it back.
    const link = document.createElement("a");
    link.href = url;
    link.download = backupFilename(todayIso());
    link.click();
    // Revoking immediately can cancel the save in some browsers; a tick is
    // enough and the object is tiny.
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    setBackupFailed(false);
    setBackupNote(
      `Saved ${backup.items.length} item${backup.items.length === 1 ? "" : "s"}.`
    );
  }

  async function restore(file: File) {
    setBackupNote(null);
    setBackupFailed(false);
    try {
      const result: ImportResult = importBackup(await file.text());
      onDataReplaced();
      setBackupNote(describeImport(result));
    } catch (error) {
      setBackupFailed(true);
      setBackupNote(
        error instanceof BackupError ? error.message : "Couldn't read that file."
      );
    }
  }

  const set = <K extends keyof Settings>(key: K) => (value: Settings[K]) =>
    update({ [key]: value } as Partial<Settings>);

  return (
    <div className="settings">
      <ChoiceRow
        label="Theme"
        hint="Minimal drops the colour from icons too."
        options={THEMES}
        labels={THEME_LABELS}
        value={settings.theme}
        onChange={set("theme")}
      />

      <ChoiceRow
        label="Goal"
        hint="Orders recipes to suit it. Nothing is hidden — cut puts the most protein per calorie first, bulk puts the biggest meals first."
        options={EATING_GOALS}
        labels={EATING_LABELS}
        value={settings.eating}
        onChange={set("eating")}
      />

      <ChoiceRow
        label="Diet"
        hint="This one does hide things."
        options={DIETS}
        labels={DIET_LABELS}
        value={settings.diet}
        onChange={set("diet")}
      />

      <ChoiceRow
        label="Cooking"
        hint="Recipes above this level are hidden."
        options={COOKING_LEVELS}
        labels={COOKING_LABELS}
        value={settings.cookingExperience}
        onChange={set("cookingExperience")}
      />

      <ChoiceRow
        label="Expiry banner"
        hint="Shown at the top of the pantry. Nothing is sent to your phone — there are no push notifications yet."
        options={EXPIRY_ALERTS}
        labels={ALERT_LABELS}
        value={settings.notificationFrequency}
        onChange={set("notificationFrequency")}
      />

      <section className="settings__block">
        <h2 className="setting__label">Photo recognition</h2>
        <p className="setting__hint">
          {isVisionConfigured()
            ? "Photos are read once and the answer is remembered, so the same photo never costs a second call. Whether a key is actually configured is a server fact — press the button to find out."
            : "Switched off in this build."}
        </p>

        {/* The button that ends guessing. It makes one real request and names
            the thing to go and fix. */}
        {isVisionConfigured() && (
          <>
            <button
              className="settings__action"
              type="button"
              disabled={checking}
              onClick={async () => {
                setChecking(true);
                setCheck(null);
                setCopied(false);
                setCheck(await checkVision());
                setChecking(false);
              }}
            >
              {checking ? (
                <>
                  <Icon
                    name="spinner"
                    className="settings__action-icon"
                    data-spinning="true"
                  />
                  Checking…
                </>
              ) : (
                "Check photo recognition"
              )}
            </button>

            {check && (
              <div className="settings__check" data-state={check.status} role="status">
                <p className="settings__check-verdict">
                  <Icon
                    name={check.status === "ok" ? "check" : "alert"}
                    className="settings__check-icon"
                  />
                  {check.status === "ok"
                    ? "Working"
                    : check.status === "off"
                      ? "Off"
                      : check.status === "small-only"
                        ? "Working, but not for receipts"
                        : `Not working — ${check.reason}${
                            check.httpStatus ? ` (HTTP ${check.httpStatus})` : ""
                          }`}
                </p>
                <p className="settings__check-detail">{check.detail}</p>

                {/* The facts, in one line, selectable. Three rounds of "it
                    doesn't work" were spent on paraphrased verdicts; a line you
                    can copy exactly removes the step where detail gets lost. */}
                <p className="settings__report">
                  <code className="settings__report-text">{check.report}</code>
                  <button
                    className="settings__copy"
                    type="button"
                    onClick={() => {
                      void navigator.clipboard?.writeText(check.report).then(
                        () => setCopied(true),
                        () => setCopied(false)
                      );
                    }}
                  >
                    {copied ? "Copied" : "Copy"}
                  </button>
                </p>
              </div>
            )}
          </>
        )}

        <button
          className="settings__action"
          type="button"
          onClick={() => {
            clearRecognitionCache();
            setCacheCleared(true);
          }}
        >
          {cacheCleared ? (
            <>
              <Icon name="check" className="settings__action-icon" />
              Cleared
            </>
          ) : (
            "Clear remembered photos"
          )}
        </button>
      </section>

      <section className="settings__block">
        <h2 className="setting__label">Your data</h2>
        <p className="setting__hint">
          Everything is stored in this browser only. Clearing your browsing data
          erases it, and there is no copy anywhere else — so keep an export
          somewhere safe.
        </p>

        <div className="settings__actions">
          <button className="settings__action" type="button" onClick={download}>
            Export a backup
          </button>
          <button
            className="settings__action"
            type="button"
            onClick={() => fileRef.current?.click()}
          >
            Restore from a backup
          </button>
        </div>

        {/* Hidden, because a bare file input can't be styled and the button
            beside it says the same thing more clearly. */}
        <input
          className="sr-only"
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          aria-label="Choose a backup file"
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Cleared so choosing the same file twice fires onChange again.
            event.target.value = "";
            if (file) void restore(file);
          }}
        />

        {backupNote && (
          <p className="settings__note" role="status">
            <Icon
              name={backupFailed ? "alert" : "check"}
              className="settings__note-icon"
              data-tone={backupFailed ? "alert" : undefined}
            />
            {backupNote}
          </p>
        )}
      </section>

      <p className="settings__footnote">
        Everything lives in this browser. Shelf lives are estimates to help you
        use food up, not food-safety advice — trust your eyes and nose.
      </p>

      {/* So a stale build can be spotted from the screen rather than guessed at. */}
      <p className="settings__build">Build {BUILD}</p>
    </div>
  );
}

/** What actually happened, in a sentence, including the parts that didn't. */
function describeImport(result: ImportResult): string {
  const items = `Restored ${result.imported} item${result.imported === 1 ? "" : "s"}`;
  const skipped =
    result.skipped > 0
      ? `, and skipped ${result.skipped} entr${result.skipped === 1 ? "y" : "ies"} that didn't look like food`
      : "";
  return `${items}${skipped}.`;
}
